import template from './ko-field-view-list.html';

class ListViewComponentVM
{
	constructor(params)
	{
		this.params = params;
		this.value = ko_helper.safe_observable(params.value);

		// If the value is a stringified object or array, parse it
		if (typeof this.params.value === 'string') {
			const trimmed = this.params.value.trim();
			if (trimmed.startsWith('{') || trimmed.startsWith('['))
			{
				try {
					this.value(JSON.parse(this.params.value));
				} catch (e) {
					// fallback: keep as string
				}
			}
		}
		this.extra_classes = ko_helper.safe_observableArray(params.extra_classes || []);
		this.options = ko.observableArray([]);
		this.optionsText = ko_helper.safe_observable(params.options_text || params.list_label_field, null);
		this.optionsValue = ko_helper.safe_observable(params.options_value || params.list_value_field, null);

		// Computed to check if we have multiple values (array)
		this.isMultipleValue = ko.pureComputed(() => {
			const val = this.value();
			return Array.isArray(val) && val.length > 0;
		});

		// Computed to get the array of values
		this.valuesArray = ko.pureComputed(() => {
			const val = this.value();
			if (Array.isArray(val)) return val;
			return [];
		});

		// Helper method to get display text for a single value
		this.getDisplayTextForValue = (value) => {
			if (value === undefined || value === null) return '';

			// If we have options, find the matching one
			const options = this.options();
			if (options.length > 0)
			{
				let matchingOption;
				if (this.optionsValue())
					matchingOption = options.find(opt => opt[this.optionsValue()] === value);
				else if (typeof value === 'object')
				{
					// For object values (like from cache), try to match the whole object
					matchingOption = options.find(opt => {
						if (this.optionsValue())
							return opt[this.optionsValue()] === value[this.optionsValue()];

						return JSON.stringify(opt) === JSON.stringify(value);
					});
				}
				else
					matchingOption = options.find(opt => opt === value);

				if (matchingOption)
				{
					// If text field specified
					if (this.optionsText())
						return matchingOption[this.optionsText()];

					// For object values without a text field
					if (typeof matchingOption === 'object')
					{
						const keys = Object.keys(matchingOption);
						const stringKey = keys.find(k => typeof matchingOption[k] === 'string' || typeof matchingOption[k] === 'number');
						return stringKey ? matchingOption[stringKey] : JSON.stringify(matchingOption);
					}

					return String(matchingOption);
				}
			}

			// If no matching option found, try to display the value directly
			if (typeof value === 'object')
			{
				// For object values, try to find a suitable display field
				if (this.optionsText() && value[this.optionsText()])
					return value[this.optionsText()];

				const keys = Object.keys(value);
				const stringKey = keys.find(k => typeof value[k] === 'string' || typeof value[k] === 'number');
				return stringKey ? value[stringKey] : JSON.stringify(value);
			}

			// Fallback to string representation
			return String(value);
		};

		// Create a computed observable that gets the display text for the selected value
		this.displayValue = ko.computed(() => {
			const value = this.value();
			if (Array.isArray(value))
				return value.map(v => this.getDisplayTextForValue(v)).filter(text => text !== '').join(', ');

			return this.getDisplayTextForValue(value);
		});

		this.load_options();
	}

	async load_options()
	{
		let rawItems = [];
		let options = this.params.ui_fields?.options || this.params.options;

		// 1. Manual options override
		if (options)
		{
			if (typeof options === 'string')
			{
				try {
					let normalized = options.trim().replace(/'/g, '"');
					rawItems = JSON.parse(normalized);
				} catch (err) {
					console.error('Invalid manual options JSON:', err);
					rawItems = [];
				}
			} else if (Array.isArray(options))
				rawItems = options;
			else if (typeof options === 'object')
			{
				if (Array.isArray(options.items))
				{
					rawItems = options.items;
					if (typeof rawItems[0] === 'string' || typeof rawItems[0] === 'number')
					{
						rawItems = rawItems.map(i => ({ text: String(i), value: i }));
						this.optionsText('text');
						this.optionsValue('value');
					}
					else if (typeof rawItems[0] === 'object')
					{
						if (!this.optionsText()) this.optionsText('text');
						if (!this.optionsValue()) this.optionsValue('value');
					}
				}
			}
		}
		else if (Array.isArray(this.params.items))  // 2. Array passed via `items`
			rawItems = this.params.items;
		else if (this.params.items_from_cache || this.params.ui_fields?.items_from_cache)  // 3. Items from cache
		{
			if (this.params.items_from_cache == 'AllCaches')
			{
				let x = Grape.cache.cache_items || {};
				rawItems = Object.keys(x);
				this.optionsText('text');
				this.optionsValue('value');
			}
			else
			{
				let cache;
				if (this.params.items_from_cache)
					cache = this.params.items_from_cache;
				else
					cache = this.params.ui_fields.items_from_cache;

				try {
					rawItems = await Grape.cache.get(cache);
				} catch (err) {
					console.error('Cache error:', err);
					rawItems = [];
				}
			}
		}
		else if (this.params.lookup || this.params.ui_fields?.lookup)  // 4. Lookup
		{
			let res = await Grape.fetches.getJSON('/api/record', {
				schema: 'grape',
				table: 'v_lookup_value',
				filter: [{ field: 'lookup_name', operand: '=', value: this.params.lookup }]
			});

			rawItems = res.records || [];
			if (!this.optionsText())  this.optionsText('name');
			if (!this.optionsValue()) this.optionsValue('lookup_value_id');
		}
		else if (this.params.fetch_items instanceof Function)  // 5. Custom fetch function
			rawItems = await this.params.fetch_items(this) || [];

		// Normalize items array
		let items = [];
		let first = rawItems[0];

		if (typeof first === 'string' || typeof first === 'number')
		{
			items = rawItems.map(i => ({ text: String(i), value: i }));
			this.optionsText('text');
			this.optionsValue('value');
		}
		else if (first && typeof first === 'object')
		{
			items = rawItems;
			if (!this.optionsText())  this.optionsText('name');
			if (!this.optionsValue()) this.optionsValue('value');
		}

		this.options(items);
	}
}

export default {
	name: 'ko-field-view-list',
	viewModel: ListViewComponentVM,
	module_type: 'ko',
	template: template
} 